import type { OperationsNode } from '../types/operations.types'
import { isTeamNode } from './nodeTypes'

/** What a node's children ARE, by hierarchy rule rather than by what it holds. */
export type ChildKind = 'team' | 'division' | 'conference' | 'none'

/**
 * i18n key naming each kind, so the tree rail and the panel's stat strip cannot
 * drift apart on wording.
 */
export const CHILD_COUNT_LABEL_KEY: Record<ChildKind, string> = {
  team: 'common:team_plural',
  division: 'common:division_plural',
  conference: 'common:conference_plural',
  // A team has no children; the label is never rendered.
  none: 'common:team_plural',
}

/**
 * Which noun labels a node's child count.
 *
 * Fixed by TYPE only for a division, which holds nothing but teams. Inferring
 * from `children` there would mislabel an EMPTY division as "0 divisions", a
 * thing a division can never contain (see `canAddChild`).
 *
 * A season and a conference both read their children instead. A season may
 * hold teams, divisions OR conferences. A conference holds divisions in data
 * created today, but older data has teams sitting directly under one — and
 * calling those "6 divisions" is exactly the mislabel this function exists to
 * prevent. Both fall back to 'division' when empty, that being the child they
 * offer to add.
 */
export const childCountKind = (node: OperationsNode): ChildKind => {
  if (isTeamNode(node)) {
    return 'none'
  }
  if (node.type === 'division') {
    return 'team'
  }

  // Season or conference. Teams and sub-competitions never coexist, so the
  // first child is representative — but a sub-competition may be a conference
  // or a division, and calling a conference a "division" is another mislabel.
  const [firstChild] = node.children
  if (!firstChild) {
    return 'division'
  }
  if (isTeamNode(firstChild)) {
    return 'team'
  }
  return firstChild.type === 'conference' ? 'conference' : 'division'
}
